import Phaser from 'phaser';
import { FartType } from './farts/FartTypes';

/**
 * BasicFart - A damage-dealing fart entity with a physics body
 */
export interface FartConfig {
    damage?: number;
    stunDuration?: number;
    startScale?: number;
    endScale?: number;
}

export class BasicFart extends Phaser.Physics.Arcade.Sprite {
    public readonly fartType: FartType = FartType.BASIC;
    private damage: number;
    private stunDuration: number;

    constructor(scene: Phaser.Scene, x: number, y: number, config: FartConfig = {}) {
        super(scene, x, y, 'fart_basic');

        this.damage = config.damage ?? 10;
        this.stunDuration = config.stunDuration ?? 500;

        const startScale = config.startScale ?? 0.5;
        const endScale = config.endScale ?? 2.5;

        scene.add.existing(this);
        scene.physics.add.existing(this);

        this.setAlpha(0.8);
        this.setScale(startScale);

        // Configure circular physics body - properly sized and centered for the current scale
        const body = this.body as Phaser.Physics.Arcade.Body;
        const baseRadius = this.width / 2;
        const scaledRadius = baseRadius * startScale;
        // Offset to center the circle on the scaled sprite
        const offset = (this.width - scaledRadius * 2) / 2;
        body.setCircle(scaledRadius, offset, offset);
        body.setImmovable(true);

        // Animation
        scene.tweens.add({
            targets: this,
            scale: endScale,
            alpha: 0,
            duration: 600 + (endScale - 2.5) * 100, // Slightly longer for bigger farts
            ease: 'Power2',
            onComplete: () => this.destroy(),
        });

        // Expansion of the physics body to match visual scale as it grows
        scene.time.addEvent({
            delay: 50,
            callback: () => {
                if (this.active && this.body) {
                    const currentRadius = baseRadius * this.scaleX;
                    const currentOffset = (this.width - currentRadius * 2) / 2;
                    body.setCircle(currentRadius, currentOffset, currentOffset);
                }
            },
            repeat: 12 // Run for the duration of the expansion
        });
    }

    getDamage(): number {
        return this.damage;
    }

    getStunDuration(): number {
        return this.stunDuration;
    }
}
